import React, { useState } from 'react';
import { useAccessibility } from '../../context/AccessibilityContext';
import { 
  Accessibility, Type, Contrast, RefreshCcw, X, 
  MousePointer2, ImageOff, Palette, AlignJustify,
  ScanLine, Link, PauseCircle, Zap
} from 'lucide-react';

const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const {
    fontSizeLevel, setFontSizeLevel,
    highContrast, toggleHighContrast,
    dyslexiaFont, toggleDyslexiaFont,
    spacingLevel, toggleSpacing,
    monochrome, toggleMonochrome,
    bigCursor, toggleBigCursor,
    hideImages, toggleHideImages,
    readingGuide, toggleReadingGuide,
    highlightLinks, toggleHighlightLinks,
    pauseAnim, togglePauseAnim,
    invertColors, toggleInvertColors,
    resetSettings
  } = useAccessibility();

  const FeatureButton = ({ active, onClick, icon: Icon, label, subLabel }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
        active 
          ? 'border-emerald-600 bg-emerald-50 text-emerald-900 shadow-sm' 
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} className={active ? "text-emerald-600" : "text-gray-500"} />
        <div className="text-left leading-tight">
          <div className="font-semibold text-sm">{label}</div>
          {subLabel && <div className="text-[10px] text-gray-500">{subLabel}</div>}
        </div>
      </div>
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
    </button>
  );

  return (
    /* ADICIONEI A CLASSE 'accessibility-panel-ignore' AQUI EMBAIXO 👇 */
    <div className="accessibility-panel-ignore fixed right-4 bottom-4 z-[1000] print:hidden font-sans">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-emerald-600 hover:bg-emerald-700 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-transform hover:scale-105"
        title="Menu de Acessibilidade"
      >
        {isOpen ? <X size={28} /> : <Accessibility size={28} />}
      </button>

      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 animate-in slide-in-from-bottom-5 overflow-hidden flex flex-col max-h-[85vh]">
          
          <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
            <h2 className="font-bold text-gray-800 flex items-center gap-2">
              <Accessibility className="text-emerald-600" /> Acessibilidade
            </h2>
            <button onClick={resetSettings} className="text-xs font-medium text-red-500 hover:text-red-700 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-100">
              <RefreshCcw size={12} /> Resetar
            </button>
          </div>

          <div className="p-3 space-y-2 overflow-y-auto custom-scrollbar">
            
            <div className="bg-gray-50 p-2 rounded-lg border border-gray-100 mb-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                <Type size={12} /> Tamanho da Fonte
              </label>
              <div className="flex items-center gap-2 bg-white p-1 rounded border">
                <button 
                  onClick={() => setFontSizeLevel(prev => Math.max(-1, prev - 1))}
                  className="p-2 hover:bg-gray-100 flex-1 rounded font-bold text-sm"
                  disabled={fontSizeLevel <= -1}
                >-A</button>
                <span className="w-8 text-center font-bold text-emerald-600 text-sm">{fontSizeLevel}</span>
                <button 
                  onClick={() => setFontSizeLevel(prev => Math.min(4, prev + 1))}
                  className="p-2 hover:bg-gray-100 flex-1 rounded font-bold text-sm"
                  disabled={fontSizeLevel >= 4}
                >+A</button>
              </div>
            </div>

            <div className="text-[10px] font-bold text-gray-400 uppercase mt-2 ml-1">Visual</div>
            <div className="grid grid-cols-1 gap-2">
              <FeatureButton active={highContrast} onClick={toggleHighContrast} icon={Contrast} label="Alto Contraste" />
              <FeatureButton active={invertColors} onClick={toggleInvertColors} icon={Zap} label="Inverter Cores" subLabel="Modo Raio-X" />
              <FeatureButton active={monochrome} onClick={toggleMonochrome} icon={Palette} label="Monocromático" subLabel="Escala de cinza" />
            </div>

            <div className="text-[10px] font-bold text-gray-400 uppercase mt-2 ml-1">Leitura & Foco</div>
            <div className="grid grid-cols-1 gap-2">
              <FeatureButton active={dyslexiaFont} onClick={toggleDyslexiaFont} icon={Type} label="Fonte Legível" subLabel="Amigável para dislexia" />
              <FeatureButton active={spacingLevel > 0} onClick={toggleSpacing} icon={AlignJustify} label="Espaçamento" subLabel={spacingLevel === 0 ? "Normal" : spacingLevel === 1 ? "Médio" : "Grande"} />
              <FeatureButton active={readingGuide} onClick={toggleReadingGuide} icon={ScanLine} label="Guia de Leitura" subLabel="Linha guia" />
              <FeatureButton active={highlightLinks} onClick={toggleHighlightLinks} icon={Link} label="Destacar Links" subLabel="Sublinhar clicáveis" />
            </div>

            <div className="text-[10px] font-bold text-gray-400 uppercase mt-2 ml-1">Navegação</div>
            <div className="grid grid-cols-1 gap-2">
              <FeatureButton active={bigCursor} onClick={toggleBigCursor} icon={MousePointer2} label="Cursor Grande" />
              <FeatureButton active={pauseAnim} onClick={togglePauseAnim} icon={PauseCircle} label="Parar Animações" />
              <FeatureButton active={hideImages} onClick={toggleHideImages} icon={ImageOff} label="Ocultar Imagens" />
            </div>

          </div>
          
          <div className="bg-gray-50 p-2 text-center border-t text-[10px] text-gray-400">
            Acessibilidade Plus v3.1
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityPanel;